import React, { useCallback, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { createContext, useContext } from "react";
import { getProduct, getVariant, PROMOS, resolveImage } from "../data/products";
import { useCatalog } from "./CatalogContext";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
// Bumped to v3 after the Feb-2026 catalog overhaul; old item slugs are gone.
const STORAGE_KEY = "luviarx_cart_v10";

const keyOf = (slug, variantId) => `${slug}::${variantId}`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const { products } = useCatalog();
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [activeSeasonal, setActiveSeasonal] = useState(null);

  const refreshSeasonal = useCallback(() => {
    axios.get(`${API}/promos/seasonal`)
      .then(({ data }) => setActiveSeasonal(data?.promo || null))
      .catch(() => setActiveSeasonal(null));
  }, []);

  useEffect(() => {
    refreshSeasonal();
    window.addEventListener("focus", refreshSeasonal);
    return () => window.removeEventListener("focus", refreshSeasonal);
  }, [refreshSeasonal]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (slug, variantId, qty = 1) => {
    setItems((prev) => {
      const product = getProduct(products, slug);
      if (!product) return prev;
      const variant = getVariant(product, variantId);
      if (!variant || !variant.inStock || variant.stock <= 0) return prev;
      const cap = Math.min(20, variant.stock);
      const k = keyOf(slug, variant.id);
      const existing = prev.find((i) => keyOf(i.slug, i.variantId) === k);
      if (existing) {
        return prev.map((i) =>
          keyOf(i.slug, i.variantId) === k
            ? { ...i, qty: Math.min(cap, i.qty + qty) }
            : i
        );
      }
      return [...prev, { slug, variantId: variant.id, qty: Math.min(cap, qty) }];
    });
    setOpen(true);
  };

  const updateQty = (slug, variantId, qty) => {
    if (qty <= 0) return removeItem(slug, variantId);
    const product = getProduct(products, slug);
    const variant = getVariant(product, variantId);
    const cap = variant ? Math.min(20, variant.stock || 0) : qty;
    const k = keyOf(slug, variantId);
    setItems((prev) =>
      prev.map((i) =>
        keyOf(i.slug, i.variantId) === k ? { ...i, qty: Math.min(cap, qty) } : i
      )
    );
  };

  const removeItem = (slug, variantId) => {
    const k = keyOf(slug, variantId);
    setItems((prev) => prev.filter((i) => keyOf(i.slug, i.variantId) !== k));
  };

  const clear = () => setItems([]);

  const detailed = useMemo(() => {
    return items
      .map((i) => {
        const product = getProduct(products, i.slug);
        if (!product) return null;
        const variant = getVariant(product, i.variantId);
        if (!variant) return null;
        const { src: image, fallback: imageFallback } = resolveImage(product, variant);
        return {
          ...product,
          image,
          imageFallback,
          variant,
          qty: i.qty,
          lineTotal: variant.price * i.qty,
        };
      })
      .filter(Boolean);
  }, [items, products]);

  const subtotal = useMemo(
    () => detailed.reduce((s, i) => s + i.lineTotal, 0),
    [detailed]
  );
  const count = useMemo(
    () => items.reduce((s, i) => s + i.qty, 0),
    [items]
  );

  // Promotions
  const starterKitThreshold = PROMOS.starterKit.threshold;
  const qualifiesStarterKit = PROMOS.starterKit.enabled && subtotal >= starterKitThreshold;
  const remainingForStarterKit = Math.max(0, starterKitThreshold - subtotal);

  // ── Volume discount (auto, based on total vials in cart) ───────────────
  // 2 vials → 5% · 3+ vials → 8%. Stacks additively with FIRST10 promo.
  // Seasonal promo (e.g. Trick or Treat) is NON-STACKABLE: when any cart
  // line qualifies, every other discount is skipped (mirrors orders.py).
  const seasonalPromo = useMemo(() => {
    if (!activeSeasonal) return null;
    const ids = activeSeasonal.product_ids || [];
    const eligibleSubtotal = +detailed
      .filter((i) => ids.includes(i.slug))
      .reduce((s, i) => s + i.lineTotal, 0).toFixed(2);
    if (eligibleSubtotal <= 0) return null;
    return {
      id: activeSeasonal.id,
      name: activeSeasonal.name,
      pct: activeSeasonal.percent,
      allowFirst10: !!activeSeasonal.allow_first10,
      eligibleSubtotal,
      amount: +(eligibleSubtotal * activeSeasonal.percent / 100).toFixed(2),
    };
  }, [activeSeasonal, detailed]);
  const seasonalAmount = seasonalPromo ? seasonalPromo.amount : 0;

  const volumeDiscountPct = seasonalPromo ? 0 : count >= 3 ? 8 : count >= 2 ? 5 : 0;
  const volumeDiscountAmount = +(subtotal * volumeDiscountPct / 100).toFixed(2);

  // Promo code (FIRST10 or admin-managed code) — captured via the post-signup
  // WelcomeOfferBanner or the "Apply promo code" form on the checkout review
  // page. Both the
  // Promo code state — supports STACKING multiple codes. `promoCodes` is
  // the source of truth; the singular `promoCode` / `promoCodePct` /
  // `promoCodeAmountOff` are computed from it for backward compat with the
  // existing checkout UI + summary. Cached in localStorage across refreshes.
  const PROMO_STORAGE_KEY = "luviarx_promo_banner_success_v1";
  const _loadPromos = () => {
    try {
      const cached = localStorage.getItem(PROMO_STORAGE_KEY);
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) return parsed;
      // Legacy shape { code, pct, amount_off } → wrap into a 1-element list.
      if (parsed && parsed.code) return [parsed];
    } catch { /* ignore */ }
    return [];
  };
  const [promoCodes, setPromoCodesState] = useState(_loadPromos);

  const _persistPromos = (list) => {
    if (typeof window === "undefined") return;
    if (!list || list.length === 0) localStorage.removeItem(PROMO_STORAGE_KEY);
    else localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(list));
  };

  // Add / replace one code. Uppercase & dedupe by code.
  const addPromoCode = (code, pct = 0, amountOff = 0) => {
    const norm = (code || "").trim().toUpperCase();
    if (!norm) return;
    const entry = {
      code: norm,
      pct: Number(pct) || 0,
      amount_off: Number(amountOff) || 0,
    };
    setPromoCodesState((prev) => {
      const others = (prev || []).filter((p) => p.code !== norm);
      const next = [...others, entry];
      _persistPromos(next);
      return next;
    });
  };
  const removePromoCode = (code) => {
    const norm = (code || "").trim().toUpperCase();
    setPromoCodesState((prev) => {
      const next = (prev || []).filter((p) => p.code !== norm);
      _persistPromos(next);
      return next;
    });
  };
  // Legacy single-code helpers — kept working for any old callers.
  const setPromoCode = (code, pct = 0, amountOff = 0) => {
    // Replace the whole list with just this code (single-code UX).
    const norm = (code || "").trim().toUpperCase();
    if (!norm) return;
    const list = [{
      code: norm,
      pct: Number(pct) || 0,
      amount_off: Number(amountOff) || 0,
    }];
    setPromoCodesState(list);
    _persistPromos(list);
  };
  const clearPromoCode = () => {
    setPromoCodesState([]);
    _persistPromos([]);
  };

  // ── Auto-apply affiliate personal discount ──────────────────────────
  // When the auth'd user is is_affiliate=true with a personal discount %,
  // the backend applies it automatically on order creation. Client-side
  // we mirror it as a synthetic "personal discount" line so the cart total
  // shown in the UI matches the final invoice.
  const affiliatePersonalPct = useMemo(() => {
    if (!user?.is_affiliate) return 0;
    return Number(user.affiliate_personal_discount_pct) || 0;
  }, [user?.is_affiliate, user?.affiliate_personal_discount_pct]);

  // Multi-coupon math (mirrors backend orders.py):
  //   • Percent codes → % of ORIGINAL subtotal (fair + predictable).
  //   • Fixed-$ codes → cap at running remaining balance.
  //   • Total promo discount capped at 60% of subtotal.
  const MAX_COUPON_PCT_OF_SUBTOTAL = 0.60;
  let _remaining = Math.max(0, subtotal - volumeDiscountAmount - seasonalAmount);
  const _appliedCoupons = [];
  // During a sale only FIRST10 survives (if the sale allows it), off the post-sale total.
  const _codesToApply = seasonalPromo
    ? promoCodes.filter((p) => seasonalPromo.allowFirst10 && p.code === "FIRST10")
    : promoCodes;
  const _pctBase = subtotal - seasonalAmount;
  for (const p of _codesToApply) {
    if ((p.amount_off || 0) > 0) {
      const amt = +Math.min(p.amount_off, _remaining).toFixed(2);
      if (amt <= 0) continue;
      _appliedCoupons.push({ ...p, discount_amount: amt, type: "amount" });
      _remaining = Math.max(0, +(_remaining - amt).toFixed(2));
    } else if ((p.pct || 0) > 0) {
      let amt = +(_pctBase * p.pct / 100).toFixed(2);
      amt = +Math.min(amt, _remaining).toFixed(2);
      if (amt <= 0) continue;
      _appliedCoupons.push({ ...p, discount_amount: amt, type: "percent" });
      _remaining = Math.max(0, +(_remaining - amt).toFixed(2));
    }
  }
  let promoCodeAmount = +_appliedCoupons.reduce(
    (s, c) => s + c.discount_amount, 0,
  ).toFixed(2);
  const _cap = +(subtotal * MAX_COUPON_PCT_OF_SUBTOTAL).toFixed(2);
  if (promoCodeAmount > _cap && _appliedCoupons.length > 0) {
    const overage = promoCodeAmount - _cap;
    for (const c of _appliedCoupons) {
      const share = c.discount_amount / promoCodeAmount;
      c.discount_amount = +Math.max(0, c.discount_amount - overage * share).toFixed(2);
    }
    promoCodeAmount = +_appliedCoupons.reduce(
      (s, c) => s + c.discount_amount, 0,
    ).toFixed(2);
  }

  // Backward-compat aliases (UI still reads these).
  const _primary = _appliedCoupons[0] || null;
  const promoCode = _primary ? _primary.code : "";
  const promoCodePct = _appliedCoupons.length === 1 && _primary.type === "percent"
    ? _primary.pct : 0;
  const promoCodeAmountOff = _appliedCoupons.length === 1 && _primary.type === "amount"
    ? _primary.amount_off : 0;
  const promoCodeType = _primary?.type || "percent";
  const promoCodeEffectivePct = promoCodePct;
  // Rich array for the multi-coupon UI (each item ready to render).
  const appliedCoupons = _appliedCoupons;

  // Personal (Friends & Family / VIP) discount — applied on top of volume +
  // promo, mirroring the server-side draft-order logic in orders.py. Only
  // active for logged-in users whose admin has toggled it on.
  const personalDiscountActive = !!user?.personal_discount_active;
  const ffPct = personalDiscountActive
    ? Math.max(0, Math.min(90, Number(user?.personal_discount_pct) || 0))
    : 0;
  // Stack the F&F % with the affiliate personal-discount % (capped at 100%
  // to mirror the backend math). If the affiliate % is active, its label
  // wins for user-facing text.
  const personalDiscountPct = seasonalPromo ? 0 : Math.min(100, ffPct + affiliatePersonalPct);
  const personalDiscountLabel = affiliatePersonalPct > 0
    ? "Affiliate discount"
    : (personalDiscountActive ? (user?.personal_discount_label || "Personal discount") : "");
  const postPromoTotal = Math.max(0, subtotal - volumeDiscountAmount - promoCodeAmount);
  const personalDiscountAmount = personalDiscountPct > 0
    ? +(postPromoTotal * personalDiscountPct / 100).toFixed(2)
    : 0;

  const totalDiscountPct = volumeDiscountPct + promoCodePct + personalDiscountPct;
  const totalDiscountAmount = +(
    volumeDiscountAmount + promoCodeAmount + personalDiscountAmount + seasonalAmount
  ).toFixed(2);
  const discountedTotal = Math.max(0, +(subtotal - totalDiscountAmount).toFixed(2));

  return (
    <CartContext.Provider
      value={{
        items,
        detailed,
        subtotal,
        count,
        addItem,
        updateQty,
        removeItem,
        clear,
        open,
        setOpen,
        // Promotions
        starterKitThreshold,
        qualifiesStarterKit,
        remainingForStarterKit,
        // Volume + promo discounts
        volumeDiscountPct,
        volumeDiscountAmount,
        promoCode,
        promoCodes,
        appliedCoupons,
        setPromoCode,
        addPromoCode,
        removePromoCode,
        clearPromoCode,
        promoCodePct,
        promoCodeAmount,
        promoCodeAmountOff,
        promoCodeType,
        promoCodeEffectivePct,
        personalDiscountActive,
        personalDiscountPct,
        personalDiscountAmount,
        personalDiscountLabel,
        totalDiscountPct,
        totalDiscountAmount,
        discountedTotal,
        activeSeasonal,
        refreshSeasonal,
        seasonalPromo,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
